/**
 * The panel's hypotheses region: what the agent suspects but has not
 * committed to.
 *
 * Plan: docs/plans/episode-agent-parity.md S9; layout in
 * docs/backend/episodes.md ("[Target] The context panel").
 *
 * A candidate link is a claim with a probability attached, and the
 * probability is the point: `works_at p 0.72` is a different thing from a
 * fact, and showing it without the number would present a guess as one.
 *
 * Anchored on the episode's primary entity, because a hypothesis is about
 * something — with no primary entity there is nothing to be uncertain about
 * and the region does not render.
 */
import { useEffect, useState, type JSX } from "react";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import { PanelRegion } from "./PanelRegion";

export interface CandidateLink {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly kind: string;
  readonly p: number;
}

export function HypothesesRegion({
  runtime,
  primaryEntityId,
  nameOf,
}: {
  readonly runtime: AppRuntime;
  readonly primaryEntityId: string | null;
  /** Resolve an entity id to something readable. Ids the panel has never seen
   *  come back as the id, which is honest — the row still says which link the
   *  agent is unsure about. */
  readonly nameOf: (id: string) => string;
}): JSX.Element | null {
  // Read through the runtime the panel already hands down, the same way
  // `useEpisodeTodo` does — the region is mounted in tests that stand up no
  // query client, and a global hook would make it unmountable there.
  const [candidates, setCandidates] = useState<readonly CandidateLink[]>([]);
  useEffect(() => {
    if (primaryEntityId === null) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    runtime.transport
      .rpc<readonly CandidateLink[]>("memory.candidates", { entity_id: primaryEntityId })
      .then((rows): void => {
        if (!cancelled) setCandidates(rows);
      })
      .catch((error: unknown) => {
        // The region stays hidden; the console says why rather than the panel
        // pretending the agent is certain about everything.
        console.warn("hypotheses read failed:", error);
      });
    return (): void => {
      cancelled = true;
    };
  }, [runtime, primaryEntityId]);

  if (candidates.length === 0) return null;

  return (
    <PanelRegion
      title="Hypotheses"
      iconName="link"
      count={String(candidates.length)}
      defaultCollapsed
      testId="hypotheses"
    >
      {candidates.map((candidate) => (
        <div key={candidate.id} className="py-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13px] text-[var(--color-agent-center-text)]">
              {nameOf(candidate.from)} → {nameOf(candidate.to)}
            </span>
            <span className="shrink-0 text-xs text-[var(--color-agent-center-text-muted)]">
              {candidate.kind}
            </span>
          </div>
          <div className="text-right text-xs text-[var(--color-agent-center-text-muted)]">
            p {candidate.p.toFixed(2)}
          </div>
        </div>
      ))}
    </PanelRegion>
  );
}
